/**
 * Chat scope — the "Ámbito" ladder (§6b).
 *
 * A chat is scoped at exactly one rung: the whole org (global), a **client** (the union of all its
 * cases' documents), or a single **case**. The scope hard-filters the user's own-document tools
 * server-side; the legal corpus is never scoped this way.
 *
 * Wire shape stays two nullable ids (`caseId` / `clientId`) rather than this union, so the field
 * added for the client rung is additive and older bodies keep working. `ChatScope` is the client-side
 * model; `toWire`/`fromWire` convert. Mutual exclusion is an invariant of the union, which is exactly
 * why the union — not the two loose refs — is what UI code reads.
 */

import type { I18nMessage } from '../workflows/i18nMessage'

export type ChatScope =
  | { kind: 'global' }
  | { kind: 'client'; id: string; name: string }
  | { kind: 'case'; id: string; name: string; clientId: string | null; clientName: string | null }

export const GLOBAL_SCOPE: ChatScope = { kind: 'global' }

export interface ChatScopeWire {
  caseId: string | null
  clientId: string | null
}

export function toWire(scope: ChatScope): ChatScopeWire {
  if (scope.kind === 'case') return { caseId: scope.id, clientId: null }
  if (scope.kind === 'client') return { caseId: null, clientId: scope.id }
  return { caseId: null, clientId: null }
}

/**
 * Rebuild a scope from persisted/wire ids. Case wins when both are somehow set — it is the more
 * specific rung, matching `scopeDocumentPredicate` server-side.
 */
export function fromWire(
  wire: Partial<ChatScopeWire> & { caseName?: string | null; clientName?: string | null },
): ChatScope {
  if (wire.caseId) {
    return {
      kind: 'case',
      id: wire.caseId,
      name: wire.caseName ?? '',
      clientId: wire.clientId ?? null,
      clientName: wire.clientName ?? null,
    }
  }
  if (wire.clientId) return { kind: 'client', id: wire.clientId, name: wire.clientName ?? '' }
  return GLOBAL_SCOPE
}

/** The label shown on the chip: `Client › Case` when both are known, else the single rung's name. */
export function scopeLabel(scope: ChatScope): string | null {
  if (scope.kind === 'global') return null
  if (scope.kind === 'client') return scope.name
  return scope.clientName ? `${scope.clientName} › ${scope.name}` : scope.name
}

/**
 * Where a workflow run launched at this rung will file its output — the sentence the proposal card
 * shows BEFORE the user commits (AlexAmin/marcus#67).
 *
 * A run's filing inputs are its `case_id` and `client_id`: the chat sends `activeCaseId`/
 * `activeClientId`, of which at most one is non-null (exactly one rung is ever active),
 * `startWorkflowRun`/`upsertWorkflowDraft` store whichever is set verbatim, and
 * `linkRunOutputToCase`/`linkRunOutputToClient` (`server/services/workflows/engine.ts`) each return
 * early when their column is null. So a `case`-scoped run files into that case's `case_documents`,
 * a `client`-scoped run files into that client's `client_documents` (AlexAmin/marcus#206 — before
 * this, a client rung had no `client_id` to carry and the run's document reached only the library),
 * and an unscoped run reaches only the library, Ejecuciones and the Activity feed.
 *
 * The client branch does NOT depend on the client's case COUNT — a one-case client still files to
 * the CLIENT, never guessing its way into that one case. The asymmetry reported in #68 was two
 * case-scoped runs; `tests/db/workflowDraftRun.test.ts` pins the absence of case-filing at one case
 * and at two.
 *
 * Lives here rather than inside `WorkflowCard.vue` for the same reason `parseStoredScope` and
 * `buildScopeGroups` do: a rule that can only be exercised by mounting an SFC with i18n and a store
 * behind it is a rule without a test.
 *
 * Deliberately NOT a sentence in a prompt file. Where a run files is per-run state, and static text
 * asserting it goes false the moment the rung moves (`services/agent-web-chat/AGENTS.md`).
 */
export interface RunFilingDestination {
  /** Whether the run's output will be linked into a case's `case_documents`. */
  filedToCase: boolean
  /**
   * Whether the run's output will be linked into a client's `client_documents`
   * (AlexAmin/marcus#206). Mutually exclusive with `filedToCase` — a run files into at most one of
   * them, matching the `workflow_runs_single_scope_rung` CHECK.
   */
  filedToClient: boolean
  /**
   * The rendered Spanish sentence, kept as the fallback `message` renders through (#208).
   *
   * Unchanged on purpose: it is what any caller that has not adopted `message` still shows, and
   * dropping it would silently blank the line instead of failing to compile.
   */
  text: string
  /**
   * The same sentence as a key, so the reader's language decides the wording rather than the
   * language this file was written in (#208, following #61's `humanizeRunFailureI18n`).
   *
   * The RULE — which rung files where — stays here and stays testable without i18n; only the
   * prose moves to the locales.
   */
  message: I18nMessage
}

export function runFilingDestination(scope: ChatScope): RunFilingDestination {
  if (scope.kind === 'case') {
    return {
      filedToCase: true,
      filedToClient: false,
      text: `Se archivará en el caso ${scope.name}.`,
      message: { key: 'chat.workflowCard.filing.case', params: { name: scope.name } },
    }
  }
  if (scope.kind === 'client') {
    return {
      filedToCase: false,
      filedToClient: true,
      text: `Se archivará en los documentos del cliente ${scope.name}.`,
      message: { key: 'chat.workflowCard.filing.client', params: { name: scope.name } },
    }
  }
  return {
    filedToCase: false,
    filedToClient: false,
    text: 'No se archivará en ningún caso. Quedará en la biblioteca de documentos.',
    message: { key: 'chat.workflowCard.filing.global' },
  }
}

/**
 * Parse a scope out of untrusted storage (see the store's remembered-scope block). Anything
 * malformed — stale shape, hand-edited JSON, a rung without an id — degrades to global rather than
 * reaching the wire, since a half-valid scope would filter the agent's documents.
 */
export function parseStoredScope(raw: string | null | undefined): ChatScope {
  if (!raw) return GLOBAL_SCOPE
  let p: any
  try {
    p = JSON.parse(raw)
  } catch {
    return GLOBAL_SCOPE
  }
  if (typeof p?.id !== 'string' || !p.id) return GLOBAL_SCOPE
  if (p.kind === 'client') return { kind: 'client', id: p.id, name: String(p.name ?? '') }
  if (p.kind === 'case') {
    return {
      kind: 'case',
      id: p.id,
      name: String(p.name ?? ''),
      clientId: typeof p.clientId === 'string' ? p.clientId : null,
      clientName: typeof p.clientName === 'string' ? p.clientName : null,
    }
  }
  return GLOBAL_SCOPE
}

export const SCOPE_STORAGE_KEY = 'marcus:chat-scope'

/**
 * The slice of the DOM `Storage` API the remembered scope needs. Named separately so the tiering
 * below is a pure function testable without a DOM — the same reason `parseStoredScope` lives here
 * rather than in the store.
 */
export interface ScopeStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

/**
 * The remembered scope lives in TWO tiers under the same key, and a read prefers the per-tab one.
 *
 * It used to be `localStorage` alone, which is shared by every tab of the origin — so picking a case
 * in one tab silently overwrote what a second tab had been working in, and the second tab restored
 * the *foreign* case on its next load. Two tabs open on two clients is the normal way this product
 * gets used (MAR-136), and there is no worse failure for a scope control than one that quietly points
 * the assistant at somebody else's documents.
 *
 * `sessionStorage` is per-tab and survives a reload, so it is the tab's own answer. `localStorage`
 * stays as the CROSS-SESSION seed, and is what preserves the behaviour the remembered scope exists
 * for in the first place: someone working a case all day should not re-pick it in a brand-new tab, or
 * tomorrow morning. A tab that has never chosen a rung has no session entry and so inherits that
 * seed; the moment it chooses one, its own choice wins for as long as the tab lives.
 *
 * A duplicated tab copies `sessionStorage`, which is also right — it is a continuation of that tab.
 */
export function readRememberedScope(session: ScopeStorage | null, local: ScopeStorage | null): ChatScope {
  const own = session?.getItem(SCOPE_STORAGE_KEY)
  return parseStoredScope(own ?? local?.getItem(SCOPE_STORAGE_KEY))
}

/**
 * Write both tiers, so the tab remembers its own rung AND the next new tab starts from the last one
 * anybody chose. Clearing removes both: a scope the user has explicitly dropped must not come back
 * from the other tier on the next load, which is the whole complaint above in mirror image.
 */
export function writeRememberedScope(
  scope: ChatScope,
  session: ScopeStorage | null,
  local: ScopeStorage | null,
): void {
  if (scope.kind === 'global') {
    session?.removeItem(SCOPE_STORAGE_KEY)
    local?.removeItem(SCOPE_STORAGE_KEY)
    return
  }
  const raw = JSON.stringify(scope)
  session?.setItem(SCOPE_STORAGE_KEY, raw)
  local?.setItem(SCOPE_STORAGE_KEY, raw)
}

/**
 * What to do with a remembered scope, given what we know about the org's visible ids.
 *
 * `knownIds === null` means the case list hasn't loaded (or its request failed) — **not** that the
 * org has no cases. The distinction is the whole point of this function: an earlier version treated
 * the two alike, so a load that raced Firebase's session restore came back empty, the remembered
 * scope looked unrecognised, and it was erased on every page load.
 */
export type RememberedScopeAction = 'restore' | 'forget' | 'wait' | 'none'

export function decideRememberedScope(
  remembered: ChatScope,
  knownIds: ReadonlySet<string> | null,
): RememberedScopeAction {
  if (remembered.kind === 'global') return 'none'
  if (!knownIds) return 'wait'
  return knownIds.has(remembered.id) ? 'restore' : 'forget'
}

export function sameScope(a: ChatScope, b: ChatScope): boolean {
  if (a.kind !== b.kind) return false
  return a.kind === 'global' || (a as { id: string }).id === (b as { id: string }).id
}
